import React, { useCallback, useMemo, useRef, useState } from 'react';
import { loadMapsLibrary } from '@/lib/googleMaps';
import { Button } from '@/components/ui/button';
import { Globe, Play, Loader2, BedDouble, Sparkles } from 'lucide-react';

// Discovery su Google Photorealistic 3D (Google Earth): mostra i PIN del viaggio
// (soggiorno + esperienze). "Carica su intento" per proteggere i costi/quota.
const validCoord = (p) => p && Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng));
const centroid = (pts) => (pts.length
  ? { lat: pts.reduce((s, p) => s + Number(p.lat), 0) / pts.length, lng: pts.reduce((s, p) => s + Number(p.lng), 0) / pts.length }
  : null);

export function TripEarthMap({ stay, experiences = [] }) {
  const wrapRef = useRef(null);
  const mapRef = useRef(null);
  const [phase, setPhase] = useState('idle'); // idle | loading | ready | error
  const [err, setErr] = useState('');

  const points = useMemo(() => {
    const out = [];
    if (validCoord(stay)) out.push({ lat: Number(stay.lat), lng: Number(stay.lng), title: stay.structure_name || 'Soggiorno', kind: 'stay' });
    (experiences || []).forEach((e) => {
      if (validCoord(e)) out.push({ lat: Number(e.lat), lng: Number(e.lng), title: e.title || 'Esperienza', kind: 'exp' });
    });
    return out;
  }, [stay, experiences]);

  const center = useMemo(() => centroid(points) || { lat: 39.33, lng: 16.53 }, [points]);

  const enter = useCallback(async () => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) { setErr('Chiave Google Maps non configurata (attiva in produzione).'); setPhase('error'); return; }
    setPhase('loading');
    try {
      window.gm_authFailure = () => { setErr('Autenticazione Google Maps fallita (chiave/referrer).'); setPhase('error'); };
    } catch (e) { /* noop */ }
    try {
      const maps3d = await loadMapsLibrary('maps3d');
      let marker = null;
      try { marker = await loadMapsLibrary('marker'); } catch (e) { /* pin opzionali */ }
      const { Map3DElement, Marker3DInteractiveElement, Marker3DElement, AltitudeMode } = maps3d;
      const PinElement = marker && marker.PinElement;

      const map = new Map3DElement({ center: { ...center, altitude: 0 }, range: 12000, tilt: 60, heading: 15 });
      try { map.mode = 'HYBRID'; } catch (e) { /* noop */ }
      map.style.width = '100%'; map.style.height = '100%';
      if (wrapRef.current) { wrapRef.current.innerHTML = ''; wrapRef.current.appendChild(map); }
      mapRef.current = map;
      setPhase('ready');

      const MarkerClass = Marker3DInteractiveElement || Marker3DElement;
      points.forEach((p) => {
        try {
          const m = new MarkerClass({
            position: { lat: p.lat, lng: p.lng, altitude: 60 },
            altitudeMode: AltitudeMode.RELATIVE_TO_GROUND,
            label: p.title,
          });
          if (PinElement) {
            const pin = p.kind === 'stay'
              ? new PinElement({ background: '#2f7d5b', borderColor: '#ffffff', glyphColor: '#ffffff', scale: 1.3 })
              : new PinElement({ background: '#e0a63b', borderColor: '#ffffff', glyphColor: '#ffffff', scale: 1.1 });
            m.append(pin);
          }
          if (Marker3DInteractiveElement) {
            m.addEventListener('gmp-click', () => {
              try { map.flyCameraTo({ endCamera: { center: { lat: p.lat, lng: p.lng, altitude: 40 }, range: 900, tilt: 66, heading: 15 }, durationMillis: 1800 }); } catch (e) { /* noop */ }
            });
          }
          map.append(m);
        } catch (e) { /* noop */ }
      });

      // volo introduttivo verso la zona del viaggio
      setTimeout(() => {
        try { map.flyCameraTo({ endCamera: { center: { ...center, altitude: 0 }, range: 6500, tilt: 62, heading: 20 }, durationMillis: 3200 }); } catch (e) { /* noop */ }
      }, 400);
    } catch (e) {
      setErr((e && e.message) || 'Google Earth 3D non disponibile.');
      setPhase('error');
    }
  }, [center, points]);

  if (points.length === 0) return null;

  return (
    <section className="space-y-3" data-testid="trip-earth">
      <h2 className="flex items-center gap-2 font-display text-lg font-semibold">
        <Globe className="h-5 w-5 text-primary" />Esplora il tuo viaggio in 3D
      </h2>
      <div className="relative h-[420px] w-full overflow-hidden rounded-2xl border border-border bg-black">
        <div ref={wrapRef} className="absolute inset-0" />

        {phase === 'idle' && (
          <button type="button" onClick={enter} data-testid="trip-earth-enter"
            className="group absolute inset-0 flex flex-col items-center justify-center gap-4 text-white">
            <div className="absolute inset-0 -z-0 bg-[linear-gradient(120deg,hsl(var(--brand-bark))_0%,hsl(var(--brand-lake))_100%)] opacity-95" />
            <span className="relative flex h-16 w-16 items-center justify-center rounded-full bg-white/15 backdrop-blur transition-transform group-hover:scale-105">
              <Play className="h-7 w-7" />
            </span>
            <div className="relative text-center">
              <p className="font-display text-xl font-semibold">Vola sul tuo itinerario</p>
              <p className="mt-1 text-sm text-white/85">{points.length} tappe · soggiorno + esperienze su Google Earth</p>
            </div>
            <span className="relative flex items-center gap-4 text-xs text-white/80">
              <span className="inline-flex items-center gap-1"><BedDouble className="h-3.5 w-3.5" />Soggiorno</span>
              <span className="inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" />Esperienze</span>
            </span>
          </button>
        )}

        {phase === 'loading' && (
          <div className="pointer-events-none absolute inset-0 z-[2] flex flex-col items-center justify-center gap-3 bg-black/40 text-white">
            <Loader2 className="h-8 w-8 animate-spin" /><p className="text-sm">Avvio del motore 3D…</p>
          </div>
        )}

        {phase === 'error' && (
          <div className="absolute inset-0 z-[2] flex flex-col items-center justify-center gap-3 bg-black/70 p-6 text-center text-white">
            <p className="font-display text-lg font-semibold">Mappa 3D non disponibile</p>
            <p className="max-w-md text-sm text-white/75">{err}</p>
            <Button variant="secondary" size="sm" className="rounded-full" onClick={() => setPhase('idle')}>Riprova</Button>
          </div>
        )}
      </div>
    </section>
  );
}

export default TripEarthMap;
